import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUp, BookOpen, ExternalLink, FolderTree, Plus, Search } from 'lucide-react';

import { adminContent } from '../../services/api';
import { MediaAsset } from 'types';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import ContentSyncNotice from '../../components/admin/ContentSyncNotice';
import { FloatingSaveBar } from '../../components/admin/FloatingSaveBar';
import type {
  QuestionLibraryCourseCatalog,
  QuestionLibrarySearchRow,
} from '../../services/api/adminContentClient';
import { LoadingAnimation } from '../../components/ui/loading-animation';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../components/ui/use-toast';
import type { DropResult } from '@hello-pangea/dnd';
import {
  AdminFlow,
  FlowMetadata,
  FlowQuestion,
} from './FlowBuilder/types';
import {
  DEFAULT_FLOW_METADATA,
  deriveQuizMode,
  FREQUENCY_OPTIONS,
  isValidEstimatedDuration,
  moduleDisplayName,
  normalizeFrequency,
  reindexQuestions as reindexFlowQuestions,
  toOrderedQuestions as toOrderedFlowQuestions,
} from './FlowBuilder/utils';
import { buildFlowPath } from 'utils/flowNavigation';
import {
  FlowBasicsFields,
  FlowQuizVoiceFields,
  FlowThemeImageFields,
} from './FlowBuilder/FlowMetadataForm';
import { FlowQuestionList } from './FlowBuilder/FlowQuestionList';
import { QuestionAddPicker } from './FlowBuilder/QuestionAddPicker';
import { ModuleCourseBrowser } from './FlowBuilder/ModuleCourseBrowser';
import { ModuleSearchPanel } from './FlowBuilder/ModuleSearchPanel';
import { ModuleReviewDialog, type ModuleQuestionReview } from './FlowBuilder/ModuleReviewDialog';
import { diffFlowMetadata, questionListSignature } from './FlowBuilder/moduleChanges';
import { useUnsavedChangesGuard } from './QuestionLibrary/useUnsavedChangesGuard';
import { type BrowserCourseId, UNASSIGNED_COURSE, parseBrowserCourseId } from './QuestionLibrary/navigatorUtils';
import { type ContentCourseRef, courseBuilderHref, questionLibraryHref } from './contentToolLinks';
import {
  CONTENT_TOOLS_QUERY_PARAMS,
  normalizePositiveIntegerParam,
  parsePositiveIntegerParam,
  type SearchParamValue,
  updateContentToolsSearchParams,
} from './contentToolsQueryParams';

export const MODULE_UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes to this module. Discard them?';

type ModuleBuilderMode = 'course' | 'all';
type EditorSectionId = 'basics' | 'quiz-voice' | 'theme-image' | 'questions';

const FB_PARAMS = CONTENT_TOOLS_QUERY_PARAMS.flowBuilder;

// Module Builder's own URL updates (tabs, course, module) stay here and confirm for themselves when they would
// replace the draft; anything that leaves Module Builder asks through the unsaved-changes guard.
const isModuleBuilderUrl = (destination: URL, current: URL) => {
  if (destination.origin !== current.origin || destination.pathname !== current.pathname) {
    return false;
  }
  const destinationTab = destination.searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.tab);
  return destinationTab === 'flow-builder' || destinationTab === current.searchParams.get(CONTENT_TOOLS_QUERY_PARAMS.tab);
};

const getQuestionTypeName = (data: Record<string, unknown>): string | undefined => {
  const displayName = data.question_type_display;
  if (typeof displayName === 'string' && displayName.trim()) {
    return displayName.trim().toLowerCase();
  }

  const questionType = data.question_type;
  if (typeof questionType === 'string' && questionType.trim()) {
    return questionType.trim().toLowerCase();
  }
  if (questionType && typeof questionType === 'object') {
    const nestedName = (questionType as { name?: unknown }).name;
    if (typeof nestedName === 'string' && nestedName.trim()) {
      return nestedName.trim().toLowerCase();
    }
  }
  return undefined;
};

const extractFlowSaveError = (error: unknown): string => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  if (!data || typeof data !== 'object') {
    return 'Unable to save module changes.';
  }

  const record = data as Record<string, unknown>;
  for (const key of ['detail', 'error', 'message']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  for (const value of Object.values(record)) {
    if (Array.isArray(value)) {
      const firstMessage = value.find((entry) => typeof entry === 'string' && entry.trim());
      if (typeof firstMessage === 'string') {
        return firstMessage;
      }
    }
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return 'Unable to save module changes.';
};

const responseStatus = (error: unknown): number | undefined => {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : undefined;
};

const EDITOR_SECTIONS: Array<{ id: EditorSectionId; label: string }> = [
  { id: 'basics', label: 'Basics' },
  { id: 'quiz-voice', label: 'Quiz & voice' },
  { id: 'theme-image', label: 'Theme & image' },
  { id: 'questions', label: 'Questions' },
];

const FlowBuilder: React.FC = () => {
  const { addToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [detailRevision, setDetailRevision] = useState(0);
  const [selected, setSelected] = useState<AdminFlow | null>(null);
  const [metadata, setMetadata] = useState<FlowMetadata>({});
  const [questionItems, setQuestionItems] = useState<FlowQuestion[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
  const creatingRef = useRef(isCreatingNew);
  creatingRef.current = isCreatingNew;
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [isAddingQuestion, setIsAddingQuestion] = useState<boolean>(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [catalog, setCatalog] = useState<QuestionLibraryCourseCatalog | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [questionReview, setQuestionReview] = useState<ModuleQuestionReview>({ status: 'unchanged' });
  const reviewRequestRef = useRef(0);
  // What the open review describes; Confirm only saves if the draft still matches it.
  const reviewedDraftRef = useRef<string | null>(null);
  // Bumped whenever the draft is replaced (another module, Discard, New module), so a slow lookup can't add
  // a question to a draft it wasn't started for.
  const draftGenerationRef = useRef(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const scrollToEditorRef = useRef(false);
  const basicsRef = useRef<HTMLElement>(null);
  const quizVoiceRef = useRef<HTMLElement>(null);
  const themeImageRef = useRef<HTMLElement>(null);
  const questionsRef = useRef<HTMLElement>(null);

  const urlSelectedFlowId = useMemo(
    () => parsePositiveIntegerParam(searchParams.get(FB_PARAMS.flowId)),
    [searchParams],
  );
  const browserCourseId = useMemo<BrowserCourseId | null>(
    () => parseBrowserCourseId(searchParams.get(FB_PARAMS.courseId)),
    [searchParams],
  );
  const mode: ModuleBuilderMode = searchParams.get(FB_PARAMS.mode) === 'all' ? 'all' : 'course';
  const selectedFlowIdRef = useRef(urlSelectedFlowId);
  selectedFlowIdRef.current = urlSelectedFlowId;

  // Each tab mounts the first time it is shown and then stays mounted (hidden while the other tab is open), so
  // switching tabs keeps its course, filters and results page. The editor is shared by both tabs.
  const [visitedModes, setVisitedModes] = useState<Record<ModuleBuilderMode, boolean>>(() => ({
    course: mode === 'course',
    all: mode === 'all',
  }));
  useEffect(() => {
    setVisitedModes((current) => (current[mode] ? current : { ...current, [mode]: true }));
  }, [mode]);

  const updateParams = useCallback(
    (updates: Record<string, SearchParamValue>) => {
      setSearchParams((current) => updateContentToolsSearchParams(current, updates), { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    adminContent.questions
      .courseCatalog()
      .then((response) => {
        if (cancelled) return;
        setCatalog(response.data as QuestionLibraryCourseCatalog);
        setCatalogError(null);
      })
      .catch(() => {
        if (!cancelled) setCatalogError('Could not load courses.');
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const applySelectedFlow = useCallback((flow: AdminFlow) => {
    draftGenerationRef.current += 1;
    setIsCreatingNew(false);
    const normalizedFrequency = normalizeFrequency(flow.frequency);
    setSelected({ ...flow, quiz_mode: deriveQuizMode(flow) });
    setMetadata({
      ...flow,
      quiz_mode: deriveQuizMode(flow),
      frequency: normalizedFrequency,
    });
    setQuestionItems(toOrderedFlowQuestions(flow.questions));
  }, []);

  const clearSelectedFlow = useCallback(() => {
    draftGenerationRef.current += 1;
    setSelected(null);
    setMetadata({});
    setQuestionItems([]);
  }, []);

  useEffect(() => {
    const rawCourseId = searchParams.get(FB_PARAMS.courseId);
    const rawFlowId = searchParams.get(FB_PARAMS.flowId);
    const rawMode = searchParams.get(FB_PARAMS.mode);

    const nextParams = updateContentToolsSearchParams(searchParams, {
      [FB_PARAMS.courseId]:
        rawCourseId === UNASSIGNED_COURSE
          ? UNASSIGNED_COURSE
          : rawCourseId
            ? normalizePositiveIntegerParam(rawCourseId)
            : null,
      [FB_PARAMS.flowId]: rawFlowId ? normalizePositiveIntegerParam(rawFlowId) : null,
      [FB_PARAMS.mode]: rawMode === 'all' ? 'all' : null,
    });

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let isCancelled = false;
    setDetailError('');
    if (urlSelectedFlowId === null) {
      setDetailLoading(false);
      if (!creatingRef.current) clearSelectedFlow();
      return;
    }
    setIsCreatingNew(false);
    clearSelectedFlow();
    setDetailLoading(true);
    void (async () => {
      try {
        const response = await adminContent.flows.retrieve(urlSelectedFlowId);
        if (!isCancelled) applySelectedFlow(response.data as AdminFlow);
      } catch (error) {
        if (!isCancelled) setDetailError('Unable to load this module. Please try again.');
      } finally {
        if (!isCancelled) setDetailLoading(false);
      }
    })();
    return () => { isCancelled = true; };
  }, [applySelectedFlow, clearSelectedFlow, detailRevision, urlSelectedFlowId]);

  const frequencySelectOptions = useMemo(() => {
    const currentValue = metadata.frequency;
    if (currentValue && normalizeFrequency(currentValue) !== currentValue.trim()) {
      const label = currentValue.replace(/_/g, ' ');
      return [...FREQUENCY_OPTIONS, { value: currentValue, label }];
    }
    return FREQUENCY_OPTIONS;
  }, [metadata.frequency]);

  const hasRoleplayQuestion = useMemo(
    () => questionItems.some((item) => item.question_type === 'roleplay'),
    [questionItems],
  );

  // What the loaded module looks like in the editor, to tell what the draft changes.
  const baselineMetadata = useMemo<FlowMetadata>(
    () => (selected ? { ...selected, frequency: normalizeFrequency(selected.frequency) } : {}),
    [selected],
  );
  const baselineQuestions = useMemo(() => toOrderedFlowQuestions(selected?.questions), [selected]);
  const baselineHasRoleplay = useMemo(
    () => baselineQuestions.some((item) => item.question_type === 'roleplay'),
    [baselineQuestions],
  );
  const metadataChanges = useMemo(
    () => (selected && !isCreatingNew
      ? diffFlowMetadata(baselineMetadata, metadata, {
        beforeHasRoleplay: baselineHasRoleplay,
        afterHasRoleplay: hasRoleplayQuestion,
      })
      : []),
    [baselineHasRoleplay, baselineMetadata, hasRoleplayQuestion, isCreatingNew, metadata, selected],
  );
  const questionsDirty = useMemo(
    () => Boolean(selected) && !isCreatingNew
      && questionListSignature(questionItems) !== questionListSignature(baselineQuestions),
    [baselineQuestions, isCreatingNew, questionItems, selected],
  );
  const isDirty = metadataChanges.length > 0 || questionsDirty;
  const createDraftDirty = useMemo(
    () => isCreatingNew && diffFlowMetadata(DEFAULT_FLOW_METADATA, metadata).length > 0,
    [isCreatingNew, metadata],
  );
  const hasUnsavedWork = isDirty || createDraftDirty;
  useUnsavedChangesGuard(hasUnsavedWork, {
    message: MODULE_UNSAVED_CHANGES_MESSAGE,
    allowNavigation: isModuleBuilderUrl,
  });

  const confirmDiscardIfDirty = useCallback(
    () => !hasUnsavedWork || window.confirm(MODULE_UNSAVED_CHANGES_MESSAGE),
    [hasUnsavedWork],
  );

  const handleModeChange = useCallback(
    (nextMode: string) => {
      updateParams({ [FB_PARAMS.mode]: nextMode === 'all' ? 'all' : null });
    },
    [updateParams],
  );

  const handleDefaultCourse = useCallback(
    (courseId: number) => {
      updateParams({ [FB_PARAMS.courseId]: courseId });
    },
    [updateParams],
  );

  const handlePickCourse = useCallback(
    (courseId: BrowserCourseId) => {
      if (String(courseId) === searchParams.get(FB_PARAMS.courseId)) {
        return;
      }
      if (!confirmDiscardIfDirty()) {
        return;
      }
      if (creatingRef.current) {
        setIsCreatingNew(false);
        clearSelectedFlow();
      }
      updateParams({ [FB_PARAMS.courseId]: courseId, [FB_PARAMS.flowId]: null });
    },
    [clearSelectedFlow, confirmDiscardIfDirty, searchParams, updateParams],
  );

  const scrollEditorIntoView = useCallback(() => {
    editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleSelectModule = useCallback(
    (flowId: number) => {
      const isSame = flowId === urlSelectedFlowId && !creatingRef.current;
      if (!isSame && !confirmDiscardIfDirty()) {
        return;
      }
      if (isSame) {
        if (mode === 'all') scrollEditorIntoView();
        return;
      }
      // Results stay above the editor on All modules: once the picked module loads, scroll down to it.
      scrollToEditorRef.current = mode === 'all';
      updateParams({ [FB_PARAMS.flowId]: flowId });
    },
    [confirmDiscardIfDirty, mode, scrollEditorIntoView, updateParams, urlSelectedFlowId],
  );

  useEffect(() => {
    if (!scrollToEditorRef.current || (!selected && !isCreatingNew)) {
      return;
    }
    scrollToEditorRef.current = false;
    scrollEditorIntoView();
  }, [isCreatingNew, scrollEditorIntoView, selected]);

  const handleBackToResults = useCallback(() => {
    resultsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleStartCreate = useCallback(() => {
    if (saving) return;
    if (!confirmDiscardIfDirty()) return;
    setIsCreatingNew(true);
    clearSelectedFlow();
    setMetadata({ ...DEFAULT_FLOW_METADATA });
    scrollToEditorRef.current = mode === 'all';
    updateParams({ [FB_PARAMS.flowId]: null });
  }, [clearSelectedFlow, confirmDiscardIfDirty, mode, saving, updateParams]);

  const handleCancelCreate = useCallback(() => {
    if (!confirmDiscardIfDirty()) {
      return;
    }
    setIsCreatingNew(false);
    clearSelectedFlow();
  }, [clearSelectedFlow, confirmDiscardIfDirty]);

  const handleDiscard = useCallback(() => {
    if (selected) {
      applySelectedFlow(selected);
    }
  }, [applySelectedFlow, selected]);

  const handleQuestionDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) {
      return;
    }
    setQuestionItems((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(sourceIndex, 1);
      if (!moved) {
        return prev;
      }
      next.splice(destinationIndex, 0, moved);
      return reindexFlowQuestions(next);
    });
  }, []);

  const handleToggleRequired = useCallback(
    (questionId: number, checked: boolean | 'indeterminate') => {
      setQuestionItems((prev) =>
        prev.map((item) =>
          item.question_id === questionId
            // A roleplay step has no skip control, so it always stays required.
            ? { ...item, is_required: item.question_type === 'roleplay' ? true : checked === true }
            : item,
        ),
      );
    },
    [],
  );

  const handleRemoveQuestion = useCallback((questionId: number) => {
    setQuestionItems((prev) =>
      reindexFlowQuestions(prev.filter((item) => item.question_id !== questionId)),
    );
  }, []);

  const appendQuestion = useCallback(
    (
      question: { id: number; key?: string; text: string; type?: string },
      reference: string,
    ): boolean => {
      if (questionItems.some((item) => item.question_id === question.id)) {
        addToast({
          title: 'Duplicate question',
          description: `Question ${reference} is already in this module.`,
          variant: 'destructive',
        });
        return false;
      }
      setQuestionItems((prev) =>
        reindexFlowQuestions([
          ...prev,
          {
            question_id: question.id,
            text: question.text,
            question_key: question.key,
            question_type: question.type,
            order: prev.length,
            is_required: true,
          },
        ]),
      );
      if (question.type === 'roleplay') {
        setMetadata((prev) => ({
          ...prev,
          is_quiz: false,
        }));
      }
      addToast({
        title: 'Question added',
        description: `Question ${question.key ?? question.id} appended to the module.`,
      });
      return true;
    },
    [addToast, questionItems],
  );

  const handleAddSearchResult = useCallback(
    (row: QuestionLibrarySearchRow) =>
      appendQuestion(
        {
          id: row.id,
          key: row.key,
          text: (row.label ?? '').trim() || `Question ${row.id}`,
          type: row.type ? row.type.trim().toLowerCase() : undefined,
        },
        row.key || String(row.id),
      ),
    [appendQuestion],
  );

  // Enter on an exact key (or a numeric ID) with nothing highlighted keeps the original lookup.
  const handleAddByKey = useCallback(async (trimmed: string): Promise<boolean> => {
    const normalizedKey = trimmed.toLowerCase();
    if (
      questionItems.some(
        (item) => (item.question_key ?? '').toLowerCase() === normalizedKey,
      )
    ) {
      addToast({
        title: 'Duplicate question',
        description: `Question ${trimmed} is already in this module.`,
        variant: 'destructive',
      });
      return false;
    }

    setIsAddingQuestion(true);
    const generation = draftGenerationRef.current;
    try {
      let data: Record<string, unknown> | undefined;
      try {
        const response = await adminContent.questions.retrieveByKey(trimmed);
        data = response.data ?? {};
      } catch (error) {
        const numericId = Number(trimmed);
        const isNumeric = Number.isInteger(numericId) && `${numericId}` === trimmed;

        if (!isNumeric || responseStatus(error) !== 404) {
          throw error;
        }

        const fallbackResponse = await adminContent.questions.retrieve(numericId);
        data = fallbackResponse.data ?? {};
      }

      if (generation !== draftGenerationRef.current) {
        // The module was discarded or replaced while the lookup ran.
        return false;
      }
      const questionId = Number((data as { id?: number }).id ?? 0);
      if (!Number.isInteger(questionId) || questionId <= 0) {
        throw new Error('Missing question id from lookup response');
      }

      const text: string =
        ((data as { text?: string }).text ?? '').trim() || `Question ${questionId}`;
      const questionKey: string | undefined =
        (data as { key?: string; question_key?: string }).key ??
        (data as { key?: string; question_key?: string }).question_key ??
        trimmed;
      return appendQuestion(
        { id: questionId, key: questionKey, text, type: getQuestionTypeName(data ?? {}) },
        trimmed,
      );
    } catch (error) {
      if (generation !== draftGenerationRef.current) return false;
      console.error('Failed to add question', error);
      addToast({
        title: 'Add question failed',
        description:
          responseStatus(error) === 404
            ? 'No question found with that key or ID.'
            : 'Unable to add that question.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsAddingQuestion(false);
    }
  }, [addToast, appendQuestion, questionItems]);

  const handleMetaField = useCallback(
    (field: keyof AdminFlow, value: string | boolean | number | null) => {
      if (
        hasRoleplayQuestion
        && value === true
        && field === 'is_quiz'
      ) {
        addToast({
          title: 'Roleplay runtime already active',
          description: 'A roleplay module cannot be a quiz.',
          variant: 'destructive',
        });
        return;
      }
      setMetadata((prev) => {
        if (field === 'is_quiz') {
          const isQuizEnabled = value === true;
          const next: FlowMetadata = { ...prev, is_quiz: isQuizEnabled };

          if (isQuizEnabled) {
            const quizMode = next.quiz_mode ?? deriveQuizMode({
              is_quiz: next.is_quiz,
              passing_score_percentage: next.passing_score_percentage,
            });
            next.quiz_mode = quizMode;
            if (quizMode === 'pass_only') {
              next.passing_score_percentage = null;
              next.max_attempts = 1;
              next.allow_retake_after_pass = false;
            } else {
              if (next.passing_score_percentage === null || next.passing_score_percentage === undefined) {
                next.passing_score_percentage = 70;
              }
              if (next.max_attempts === null || next.max_attempts === undefined) {
                next.max_attempts = 2;
              }
            }
            if (next.allow_retake_after_pass === undefined) {
              next.allow_retake_after_pass = false;
            }
            if (next.randomize_question_order === undefined) {
              next.randomize_question_order = true;
            }
            if (next.randomize_option_order === undefined) {
              next.randomize_option_order = true;
            }
          }

          return next;
        }

        if (field === 'quiz_mode') {
          const quizMode = value === 'pass_only' ? 'pass_only' : 'graded';
          const next: FlowMetadata = { ...prev, quiz_mode: quizMode };
          if (quizMode === 'pass_only') {
            next.passing_score_percentage = null;
            next.max_attempts = 1;
            next.allow_retake_after_pass = false;
          } else {
            if (next.passing_score_percentage === null || next.passing_score_percentage === undefined) {
              next.passing_score_percentage = 70;
            }
            if (next.max_attempts === null || next.max_attempts === undefined) {
              next.max_attempts = 2;
            }
          }
          return next;
        }

        return { ...prev, [field]: value };
      });
    },
    [addToast, hasRoleplayQuestion],
  );

  const handleMediaSelection = useCallback((asset: MediaAsset | null) => {
    setMetadata((prev) => ({ ...prev, media: asset, media_asset_id: asset?.id ?? null }));
  }, []);

  /** Validates the draft (toasting the first problem) and builds the save payload; null blocks saving. */
  const buildSavePayload = useCallback((): Record<string, unknown> | null => {
    const trimmedName = (metadata.name ?? '').trim();
    const trimmedTitle = (metadata.title ?? '').trim();
    const frequencyValue = (metadata.frequency ?? '').trim();
    const isQuizEnabled = !hasRoleplayQuestion && metadata.is_quiz === true;
    const inferredQuizMode =
      metadata.quiz_mode ??
      (metadata.is_quiz === true && metadata.passing_score_percentage === null
        ? 'pass_only'
        : 'graded');
    const isPassOnlyMode = isQuizEnabled && inferredQuizMode === 'pass_only';

    if (!trimmedName) {
      addToast({
        title: 'Name required',
        description: 'Please provide a name before saving.',
        variant: 'destructive',
      });
      return null;
    }

    if (!frequencyValue) {
      addToast({
        title: 'Frequency required',
        description: 'Please provide a frequency before saving.',
        variant: 'destructive',
      });
      return null;
    }

    if (trimmedTitle.length > 100) {
      addToast({
        title: 'Title too long',
        description: 'Learner-facing titles must be 100 characters or fewer.',
        variant: 'destructive',
      });
      return null;
    }

    if (!isValidEstimatedDuration(metadata.estimated_duration_minutes)) {
      addToast({
        title: 'Invalid estimated duration',
        description: 'Enter a whole number of minutes greater than zero, or leave blank.',
        variant: 'destructive',
      });
      return null;
    }
    const durationText = String(metadata.estimated_duration_minutes ?? '').trim();
    const estimatedDuration = durationText === '' ? null : Number(durationText);

    const passingScoreRaw =
      metadata.passing_score_percentage === null || metadata.passing_score_percentage === undefined
        ? null
        : Number(metadata.passing_score_percentage);
    const maxAttemptsRaw =
      metadata.max_attempts === null || metadata.max_attempts === undefined
        ? null
        : Number(metadata.max_attempts);

    if (isQuizEnabled && !isPassOnlyMode) {
      if (passingScoreRaw === null || Number.isNaN(passingScoreRaw) || passingScoreRaw < 0 || passingScoreRaw > 100) {
        addToast({
          title: 'Invalid passing score',
          description: 'Passing score must be between 0 and 100.',
          variant: 'destructive',
        });
        return null;
      }
      if (maxAttemptsRaw !== null && (!Number.isInteger(maxAttemptsRaw) || maxAttemptsRaw < 1)) {
        addToast({
          title: 'Invalid max attempts',
          description: 'Max attempts must be at least 1, or blank for unlimited.',
          variant: 'destructive',
        });
        return null;
      }
    }

    const payload: Record<string, unknown> = {
      name: trimmedName,
      title: trimmedTitle,
      description: metadata.description ?? '',
      is_active: metadata.is_active ?? true,
      is_public: metadata.is_public ?? false,
      frequency: normalizeFrequency(frequencyValue),
      is_quiz: isQuizEnabled,
      voice_mode_enabled: metadata.voice_mode_enabled === true,
      passing_score_percentage: isQuizEnabled ? (isPassOnlyMode ? null : passingScoreRaw) : null,
      max_attempts: isQuizEnabled ? (isPassOnlyMode ? 1 : maxAttemptsRaw) : null,
      allow_retake_after_pass: isQuizEnabled
        ? (isPassOnlyMode ? false : metadata.allow_retake_after_pass ?? false)
        : false,
      randomize_question_order: isQuizEnabled ? metadata.randomize_question_order ?? true : true,
      randomize_option_order: isQuizEnabled ? metadata.randomize_option_order ?? true : true,
      estimated_duration_minutes: estimatedDuration,
    };
    // Send the stored theme as-is: '' inherits the course theme, and a value this
    // build doesn't know (e.g. a theme added by a newer release) must survive
    // unrelated edits. The picker only replaces it when the admin chooses one.
    // New flows inherit unless the admin picked a theme.
    const themeValue = metadata.theme ?? (isCreatingNew ? '' : undefined);
    if (typeof themeValue === 'string') {
      payload.theme = themeValue;
    }
    payload.media_asset_id = metadata.media?.id ?? metadata.media_asset_id ?? null;
    return payload;
  }, [addToast, hasRoleplayQuestion, isCreatingNew, metadata]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    const payload = buildSavePayload();
    if (!payload) {
      return false;
    }

    setSaving(true);
    try {
      if (isCreatingNew) {
        const response = await adminContent.flows.create(payload);
        if (!mountedRef.current) return true;
        const created = response.data as AdminFlow;
        const createdName = created?.name ?? String(payload.name);

        setRefreshToken((current) => current + 1);
        // Another module was picked while this one was being created: keep that selection.
        if (!creatingRef.current || selectedFlowIdRef.current !== null) return true;
        setIsCreatingNew(false);
        updateParams({ [FB_PARAMS.flowId]: created.id });
        addToast({ title: 'Module created', description: createdName });
      } else if (selected) {
        await adminContent.flows.update(selected.id, payload);
        const ordered = toOrderedFlowQuestions(questionItems);
        await adminContent.flows.setQuestions(
          selected.id,
          ordered.map((item) => ({
            question_id: item.question_id,
            is_required: item.is_required,
          })),
        );
        addToast({
          title: 'Module saved',
          description: selected.affected_factory_courses?.length
            ? 'Saved on the site. Affected Course Factory courses will reconcile this revision before their next build.'
            : String(payload.name),
        });
        setRefreshToken((current) => current + 1);
        const refreshed = (await adminContent.flows.retrieve(selected.id)).data as AdminFlow;
        const normalizedRefreshed = {
          ...refreshed,
          quiz_mode: deriveQuizMode(refreshed),
          frequency: normalizeFrequency(refreshed.frequency),
        };
        if (selectedFlowIdRef.current === selected.id) applySelectedFlow(normalizedRefreshed);
      }
      return true;
    } catch (error) {
      if (!mountedRef.current) return false;
      console.error('Failed to save flow', error);
      addToast({
        title: 'Save failed',
        description: extractFlowSaveError(error),
        variant: 'destructive',
      });
      return false;
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  }, [addToast, applySelectedFlow, buildSavePayload, isCreatingNew, questionItems, selected, updateParams]);

  const draftSignature = useMemo(
    () => `${JSON.stringify(metadata)}|${questionListSignature(questionItems)}`,
    [metadata, questionItems],
  );

  const handleOpenReview = useCallback(async () => {
    if (!selected || isCreatingNew || isAddingQuestion || !buildSavePayload()) {
      return;
    }
    reviewRequestRef.current += 1;
    const requestId = reviewRequestRef.current;
    reviewedDraftRef.current = draftSignature;
    setReviewOpen(true);
    if (!questionsDirty) {
      setQuestionReview({ status: 'unchanged' });
      return;
    }
    setQuestionReview({ status: 'loading' });
    try {
      const ordered = toOrderedFlowQuestions(questionItems);
      const response = await adminContent.flows.previewQuestions(
        selected.id,
        ordered.map((item) => ({
          question_id: item.question_id,
          is_required: item.is_required,
        })),
      );
      if (reviewRequestRef.current !== requestId) return;
      const diff = (response.data as { diff?: unknown } | undefined)?.diff;
      setQuestionReview({
        status: 'ready',
        diff: Array.isArray(diff) ? diff.filter((item): item is string => typeof item === 'string') : [],
      });
    } catch (error) {
      if (reviewRequestRef.current !== requestId) return;
      console.error('Preview failed', error);
      setQuestionReview({ status: 'error' });
    }
  }, [buildSavePayload, draftSignature, isAddingQuestion, isCreatingNew, questionItems, questionsDirty, selected]);

  const closeReview = useCallback(() => {
    reviewRequestRef.current += 1;
    reviewedDraftRef.current = null;
    setReviewOpen(false);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    if (isAddingQuestion || reviewedDraftRef.current !== draftSignature) {
      // The draft changed after the review opened (for example, a question lookup finished): show the new review.
      addToast({
        title: 'Changes updated',
        description: 'The module changed while you were reviewing. Check the updated changes, then confirm.',
      });
      void handleOpenReview();
      return;
    }
    if (await handleSave()) {
      closeReview();
    }
  }, [addToast, closeReview, draftSignature, handleOpenReview, handleSave, isAddingQuestion]);

  const scrollToSection = useCallback((section: EditorSectionId) => {
    const target = {
      basics: basicsRef,
      'quiz-voice': quizVoiceRef,
      'theme-image': themeImageRef,
      questions: questionsRef,
    }[section].current;
    target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);

  const isSaving = saving;
  const canManageQuestions = Boolean(selected) && !isCreatingNew;
  const existingQuestionIds = useMemo(
    () => new Set(questionItems.map((item) => item.question_id)),
    [questionItems],
  );
  const displayName = selected ? moduleDisplayName(selected).name : '';
  const moduleCourses = selected?.theme_courses;
  // The course the open module is shown in: the By course selection when the module is in it, else its first
  // course, else "not in any course". Links into the Question Library and Course Builder keep this context.
  const moduleContextCourse: ContentCourseRef | null = !selected || !moduleCourses
    ? null
    : moduleCourses.length === 0
      ? UNASSIGNED_COURSE
      : mode === 'course' && typeof browserCourseId === 'number' && moduleCourses.some(course => course.id === browserCourseId)
        ? browserCourseId
        : moduleCourses[0].id;

  const sectionCard = (
    id: EditorSectionId,
    title: string,
    ref: React.RefObject<HTMLElement>,
    children: React.ReactNode,
  ) => (
    <section
      ref={ref}
      aria-labelledby={`fb-section-${id}`}
      className="scroll-mt-4 space-y-4 rounded-3xl bg-white/40 p-4"
      data-testid={`fb-section-${id}`}
    >
      <h3 id={`fb-section-${id}`} className="text-base font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );

  const editorPlaceholder = (
    <div
      className="flex min-h-[16rem] flex-col items-center justify-center gap-3 rounded-3xl bg-white/30 p-8 text-center"
      data-testid="fb-editor-placeholder"
    >
      <p className="text-base font-semibold text-foreground">
        {mode === 'course' ? 'Pick a module from the course' : 'Pick a module above'}
      </p>
      <p className="max-w-sm text-sm text-muted-foreground">
        {mode === 'course'
          ? 'Modules are listed in the order learners see them.'
          : 'The editor opens here, under the results.'}
      </p>
      <Button type="button" variant="secondary" size="auto" className="px-4 py-2" onClick={handleStartCreate}>
        <Plus className="h-4 w-4" aria-hidden="true" /> New module
      </Button>
    </div>
  );

  const editor = detailLoading ? (
    <Card disableAnimation maxWidth="none"><CardContent className="flex min-h-[240px] items-center justify-center"><LoadingAnimation /><span className="sr-only">Loading module details</span></CardContent></Card>
  ) : detailError ? (
    <Card disableAnimation maxWidth="none"><CardContent><p role="alert">{detailError}</p><Button onClick={() => setDetailRevision(value => value + 1)}>Retry module details</Button></CardContent></Card>
  ) : selected || isCreatingNew ? (
    <Card disableAnimation disableGlass maxWidth="none" className="min-w-0" data-testid="fb-editor">
      <CardHeader className="space-y-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-muted-foreground" data-testid="fb-editor-context">
          {mode === 'all' ? (
            <Button type="button" variant="secondary" size="auto" className="px-3 py-1.5" onClick={handleBackToResults}>
              <ArrowUp className="h-4 w-4" aria-hidden="true" /> Back to results
            </Button>
          ) : null}
          {!isCreatingNew && moduleCourses ? (
            moduleCourses.length ? (
              <span className="min-w-0 truncate" data-testid="fb-editor-courses">
                In{' '}
                {moduleCourses.slice(0, 2).map((course, index) => (
                  <span key={course.id}>
                    {index > 0 ? ', ' : ''}
                    <Link
                      to={courseBuilderHref(course.id)}
                      className="font-semibold text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      title="Open in Course Builder"
                    >
                      {course.name}
                    </Link>
                  </span>
                ))}
                {moduleCourses.length > 2 ? ` +${moduleCourses.length - 2}` : ''}
              </span>
            ) : (
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700" data-testid="fb-editor-courses">
                Not in any course
              </span>
            )
          ) : null}
          <span className="flex-1" />
          {!isCreatingNew && selected ? (
            <Button asChild variant="secondary" size="auto">
              <Link to={questionLibraryHref({ courseId: moduleContextCourse, flowId: selected.id })}>
                <BookOpen className="h-4 w-4" aria-hidden="true" /> Open in Question Library
              </Link>
            </Button>
          ) : null}
          {!isCreatingNew && selected ? (
            <Button
              variant="default"
              onClick={() => window.open(buildFlowPath({ flowName: selected.name }), '_blank')}
              size='auto'
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" /> Go To Module
            </Button>
          ) : null}
        </div>
        <div className="min-w-0 break-words">
          <CardTitle title={isCreatingNew ? 'Create new module' : displayName} />
        </div>
        <nav className="flex flex-wrap gap-2" aria-label="Module sections">
          {EDITOR_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => scrollToSection(section.id)}
              className="rounded-full border border-foundation-calm-blue-200 bg-white/70 px-3 py-1 text-xs font-medium text-foundation-base-darkbase-dark-500 hover:bg-white"
              data-testid={`fb-jump-${section.id}`}
            >
              {section.id === 'questions' && canManageQuestions
                ? `${section.label} · ${questionItems.length}`
                : section.label}
            </button>
          ))}
        </nav>
      </CardHeader>
      <CardContent className="w-full items-stretch space-y-4">
        {!isCreatingNew ? (
          <ContentSyncNotice
            impacts={selected?.affected_factory_courses}
            subjectLabel="module"
          />
        ) : null}
        {sectionCard('basics', 'Basics', basicsRef, (
          <FlowBasicsFields
            metadata={metadata}
            onFieldChange={handleMetaField}
            frequencyOptions={frequencySelectOptions}
            isSaving={isSaving}
          />
        ))}
        {sectionCard('quiz-voice', 'Quiz & voice', quizVoiceRef, (
          <FlowQuizVoiceFields
            metadata={metadata}
            onFieldChange={handleMetaField}
            isSaving={isSaving}
            hasRoleplayQuestion={hasRoleplayQuestion}
          />
        ))}
        {sectionCard('theme-image', 'Theme & image', themeImageRef, (
          <FlowThemeImageFields
            metadata={metadata}
            onFieldChange={handleMetaField}
            onMediaChange={handleMediaSelection}
            isSaving={isSaving}
          />
        ))}
        {sectionCard('questions', 'Questions', questionsRef, canManageQuestions ? (
          <>
            <p className="text-xs text-muted-foreground">
              Drag to reorder. Search by text or key to add a question, or press Enter on an exact key or ID.
            </p>
            <QuestionAddPicker
              existingQuestionIds={existingQuestionIds}
              onAddResult={handleAddSearchResult}
              onAddByKey={handleAddByKey}
              submitting={isAddingQuestion}
              disabled={isSaving}
            />
            {hasRoleplayQuestion ? (
              <p className="text-xs text-muted-foreground">
                Roleplay practice runs as a step of this module. Quiz Mode and standard Module Voice stay off while it is attached.
              </p>
            ) : null}
            <FlowQuestionList
              questions={questionItems}
              onDragEnd={handleQuestionDragEnd}
              onRemoveQuestion={handleRemoveQuestion}
              onToggleRequired={handleToggleRequired}
              courseId={moduleContextCourse}
              flowId={selected?.id ?? null}
            />
          </>
        ) : (
          <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-sm text-muted-foreground">
            Save the module first to manage question assignments.
          </div>
        ))}
        {isCreatingNew ? (
          <div className="flex flex-col flex-wrap items-center gap-2">
            <Button className="w-full max-w-full whitespace-normal sm:w-64" variant="default" onClick={() => void handleSave()} disabled={isSaving}>
              {isSaving ? 'Creating...' : 'Create Module'}
            </Button>
            <Button variant="ghost" onClick={handleCancelCreate} className="w-full max-w-full whitespace-normal sm:w-64" disabled={isSaving}>
              Cancel
            </Button>
          </div>
        ) : (
          <FloatingSaveBar
            dirty={isDirty}
            canSave={isDirty && !isAddingQuestion}
            saving={isSaving}
            onDiscard={handleDiscard}
            onSave={() => void handleOpenReview()}
            saveLabel="Review & save"
            watchRef={editorRef}
            testId="fb-save-bar"
          />
        )}
      </CardContent>
    </Card>
  ) : editorPlaceholder;

  return (
    <div className="space-y-4" data-testid="fb-workspace">
      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={mode} onValueChange={handleModeChange}>
          <TabsList>
            <TabsTrigger value="course" className="gap-2 px-3" data-testid="fb-mode-course">
              <FolderTree className="h-4 w-4" aria-hidden="true" /> By course
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2 px-3" data-testid="fb-mode-all">
              <Search className="h-4 w-4" aria-hidden="true" /> All modules
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div
        className={
          mode === 'course'
            ? 'grid grid-cols-1 items-start gap-4 xl:grid-cols-[20rem_minmax(0,1fr)]'
            : 'grid grid-cols-1 gap-4'
        }
      >
        {visitedModes.course || mode === 'course' ? (
          // `contents` keeps the browser itself as the grid item, so its sticky layout still works.
          <div className={mode === 'course' ? 'contents' : undefined} hidden={mode !== 'course'} data-testid="fb-course-tab">
            <ModuleCourseBrowser
              catalog={catalog}
              catalogError={catalogError}
              courseId={browserCourseId}
              selectedFlowId={isCreatingNew ? null : urlSelectedFlowId}
              refreshToken={refreshToken}
              onDefaultCourse={handleDefaultCourse}
              onPickCourse={handlePickCourse}
              onSelectModule={handleSelectModule}
              onCreateModule={handleStartCreate}
            />
          </div>
        ) : null}
        {visitedModes.all || mode === 'all' ? (
          <div ref={resultsRef} className="min-w-0 scroll-mt-4" hidden={mode !== 'all'} data-testid="fb-results">
            <ModuleSearchPanel
              selectedFlowId={isCreatingNew ? null : urlSelectedFlowId}
              refreshToken={refreshToken}
              courses={catalog?.courses ?? []}
              onSelectModule={handleSelectModule}
              onCreateModule={handleStartCreate}
            />
          </div>
        ) : null}
        {/* Same element in both tabs, so switching tabs never remounts the editor or drops the draft. */}
        <div ref={editorRef} className="min-w-0 scroll-mt-4">{editor}</div>
      </div>

      <ModuleReviewDialog
        open={reviewOpen}
        moduleName={displayName}
        courseCount={moduleCourses?.length ?? 0}
        changes={metadataChanges}
        questions={questionReview}
        saving={isSaving}
        onConfirm={() => void handleConfirmSave()}
        onClose={closeReview}
      />
    </div>
  );
};

export default FlowBuilder;
